/**
 * Unified Event Types (v2.0)
 *
 * Frontend type definitions matching the backend UnifiedEventEnvelope.
 * Used by the unified event consumer hook and notification store.
 */

// === Event Channels ===
export type EventChannel =
  | "execution"
  | "assets"
  | "notifications"
  | "system"
  | "activity"
  | "entitlements";

// === Unified Event Envelope (matches backend UnifiedEventEnvelope) ===
export interface UnifiedEvent<T = unknown> {
  envelopeId: string;
  eventType: string;
  channel: EventChannel;
  data: T;
  metadata: EventMetadata;
  version: "2.0";
}

export interface EventMetadata {
  timestamp: string;
  correlationId: string;
  eventId: string;
  userId: string;
  projectId?: string;
  workflowId?: string;
  workflowVersionId?: string;
  assetId?: string;
  executionId?: string;
  nodeExecutionId?: string;
  teamId?: string;
}

// === Connection State ===
export type ConnectionStatus =
  "disconnected" | "connecting" | "connected" | "reconnecting";

// === Event Type Constants ===
export const EVENT_TYPES = {
  // Execution
  WORKFLOW_STARTED: "workflow.execution.started",
  WORKFLOW_PROGRESS: "workflow.execution.progress",
  WORKFLOW_COMPLETED: "workflow.execution.completed",
  WORKFLOW_FAILED: "workflow.execution.failed",
  WORKFLOW_CANCELLED: "workflow.execution.cancelled",
  NODE_STARTED: "node.execution.started",
  NODE_PROGRESS: "node.execution.progress",
  NODE_COMPLETED: "node.execution.completed",
  NODE_FAILED: "node.execution.failed",

  // Assets
  ASSET_CREATED: "asset.created",
  ASSET_THUMBNAIL_READY: "asset.thumbnail.ready",
  ASSET_DERIVED_READY: "asset.derived.ready",
  ASSET_DERIVED_FAILED: "asset.derived.failed",
  DERIVED_COMPLETED: "derived.processing.completed",
  DERIVED_FAILED: "derived.processing.failed",

  // Notifications
  NOTIFICATION_CREATED: "notification.created",
  NOTIFICATION_READ: "notification.read",
  NOTIFICATION_DELETED: "notification.deleted",
  NOTIFICATION_BATCH_READ: "notification.batch.read",

  // System
  SYSTEM_ANNOUNCEMENT: "system.announcement",
  SYSTEM_MAINTENANCE: "system.maintenance.scheduled",

  // Email Settings
  EMAIL_SETTINGS_UPDATED: "email.settings.updated",

  // Subscription lifecycle (arrive on the activity channel; the ledger slug in
  // `data.eventTypeSlug` is `subscription-changed` / `subscription-payment-failed`).
  SUBSCRIPTION_CHANGED: "activity.subscription-changed",
  SUBSCRIPTION_PAYMENT_FAILED: "activity.subscription-payment-failed",

  // Locks (mirrors api-alpha src/services/lockBroadcast.ts LOCK_EVENT_TYPES)
  WORKFLOW_LOCK_ACQUIRED: "workflow.lock-acquired",
  WORKFLOW_LOCK_RELEASED: "workflow.lock-released",
  TIMELINE_LOCK_ACQUIRED: "timeline.lock-acquired",
  TIMELINE_LOCK_RELEASED: "timeline.lock-released",
} as const;

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

// === Notification Data Types ===
export interface NotificationCreatedData {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  severity: "info" | "success" | "warning" | "error";
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationReadData {
  notificationId: string;
  readAt: string;
}

export interface NotificationBatchReadData {
  markedCount: number;
  readAt: string;
}

export interface NotificationDeletedData {
  notificationId: string;
}

// === Asset Data Types ===
export interface DerivedAssetCompletedData {
  derivedAssetId: string;
  assetId: string;
  projectId: string;
  ruleKey: string;
  fileCount: number;
  totalFileSize: number;
  completedAt: string;
}

export interface AssetThumbnailReadyData {
  assetId: string;
  projectId: string;
  thumbnailUrl: string;
  dimensions: { width: number; height: number };
}

// === System Data Types ===
export interface SystemAnnouncementData {
  announcementId: string;
  title: string;
  message: string;
  severity: "info" | "warning" | "error";
  actionUrl?: string;
  actionLabel?: string;
  dismissible: boolean;
  expiresAt?: string;
}

// === Type Guards ===
export function isUnifiedEvent(event: unknown): event is UnifiedEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "envelopeId" in event &&
    "version" in event &&
    (event as UnifiedEvent).version === "2.0"
  );
}

export function isLegacyEvent(event: unknown): boolean {
  return (
    typeof event === "object" &&
    event !== null &&
    "eventType" in event &&
    (!("version" in event) || (event as { version: string }).version === "1.0")
  );
}

// === Channel Detection ===
export function getEventChannel(eventType: string): EventChannel {
  if (eventType.startsWith("workflow.") || eventType.startsWith("node.")) {
    return "execution";
  }
  if (
    eventType.startsWith("asset.") ||
    eventType.startsWith("derived.") ||
    eventType.startsWith("derived-asset.")
  ) {
    return "assets";
  }
  if (eventType.startsWith("notification.")) {
    return "notifications";
  }
  if (eventType.startsWith("system.") || eventType.startsWith("email.")) {
    return "system";
  }
  if (eventType.startsWith("activity.")) {
    return "activity";
  }
  if (eventType.startsWith("entitlement.")) {
    return "entitlements";
  }
  return "execution";
}
